/**
 * LiveTile — renders an HLS-based live feed from a real ONVIF camera.
 * The parent (VideoTile wrapper) decides when to mount this.
 */
import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import Hls from "hls.js";
import { API } from "@/lib/api";
import { VideoOff } from "lucide-react";

const LiveTile = forwardRef(function LiveTile({ camera }, ref) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(false);

  useImperativeHandle(ref, () => ({
    videoEl: () => videoRef.current,
  }));

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    setError(false);
    const token = localStorage.getItem("cctv_token");
    const src = `${API}/cameras/${camera.id}/hls/index.m3u8?auth=${encodeURIComponent(token || "")}`;

    if (Hls.isSupported()) {
      const hls = new Hls({ lowLatencyMode: true, liveDurationInfinity: true });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(v);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) setError(true);
      });
    } else if (v.canPlayType("application/vnd.apple.mpegurl")) {
      v.src = src;
    } else {
      setError(true);
    }
    return () => {
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch (_) { /* noop */ }
        hlsRef.current = null;
      }
    };
  }, [camera.id]);

  if (error) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-zinc-700 bg-black">
        <VideoOff size={28} />
        <div className="mono text-[10px] uppercase tracking-widest">Live Stream Error</div>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      data-testid={`live-video-${camera.channel}`}
      autoPlay
      muted
      playsInline
      className="w-full h-full object-cover"
    />
  );
});

export default LiveTile;
